/**
 * Related Products Component
 * Loads and displays related products using CLIP visual similarity API
 * Renders products on frontend using JSON data
 */

import { fetchSimilarProducts } from '@theme/vespa-search';

/**
 * Build HTML for a single product card from JSON data
 * Uses the theme's expected HTML structure with card-gallery, mock-image classes
 */
function buildProductCardHtml(product, index = 0) {
  const url = product.url || `/products/${extractHandle(product)}`;
  const title = product.title || '';
  const imageUrl = product.image_url || product.imageUrl || '';
  const productId = product.product_id || product.id || '';
  const handle = extractHandle(product);
  const loading = index < 4 ? 'eager' : 'lazy';
  const fetchpriority = index === 0 ? 'high' : 'auto';

  // Price formatting
  const fromPrice = product.from_price;
  const compareAtPrice = product.from_compare_at_price;
  const basePricing = product.base_pricing || product.variants?.[0]?.configurator?.basePricing;
  const hasValidPrice = fromPrice && fromPrice > 0;
  const hasCompareAt = compareAtPrice && compareAtPrice > fromPrice;

  // Mock image rendering - uses theme's mock-image structure
  const mockConfig = product.mock_config;
  const imageHtml = mockConfig && imageUrl
    ? buildMockImageHtml(imageUrl, mockConfig, title, loading, fetchpriority)
    : buildSimpleImageHtml(imageUrl, title, loading, fetchpriority);

  // Build price HTML - matches theme's price.liquid structure
  const priceHtml = hasValidPrice
    ? `<product-price data-product-id="${escapeHtml(productId)}">
        <div ref="priceContainer">
          <span class="from-label">From</span>
          <span class="price">${formatPrice(fromPrice, basePricing)}</span>
          ${hasCompareAt ? `<span class="compare-at-price">${formatPrice(compareAtPrice, basePricing)}</span>` : ''}
        </div>
      </product-price>`
    : '';

  // Generate a unique id for the product card link (required for navigation)
  const linkId = `product-card-link-${handle}-${index}`;

  return `
    <div class="resource-list__item">
      <product-card class="product-card" data-product-id="${escapeHtml(productId)}" data-product-handle="${escapeHtml(handle)}">
        <a id="${escapeHtml(linkId)}" href="${escapeHtml(url)}" class="product-card__link" ref="productCardLink">
          <span class="visually-hidden">${escapeHtml(title)}</span>
        </a>
        <div class="product-card__content layout-panel-flex layout-panel-flex--column product-grid__card spacing-style border-style gap-style" style="--product-card-gap: 4px;">
          <div class="card-gallery spacing-style border-style" style="position: relative;">
            ${imageHtml}
          </div>
          <div class="product-card__title" style="padding-block-start: 4px;">
            <a href="${escapeHtml(url)}" ref="productTitleLink">${escapeHtml(title)}</a>
          </div>
          ${priceHtml}
        </div>
      </product-card>
    </div>
  `;
}

/**
 * Build HTML for product grid from array of products
 */
function buildProductGridHtml(products) {
  return products.map((product, index) => buildProductCardHtml(product, index)).join('');
}

/**
 * Build mock image HTML with frame overlay
 * Uses theme's mock-image-container CSS structure for proper styling
 */
function buildMockImageHtml(artworkUrl, mockConfig, alt, loading, fetchpriority) {
  const frameUrl = mockConfig.image || '';
  const placementTop = mockConfig.placementTop || mockConfig.placement_top || 0;
  const placementLeft = mockConfig.placementLeft || mockConfig.placement_left || 0;
  const placementWidth = mockConfig.placementWidth || mockConfig.placement_width || 100;
  const aspectRatio = mockConfig.aspectRatio || mockConfig.aspect_ratio || 'auto';
  const frameAspectRatio = mockConfig.frameAspectRatio || mockConfig.frame_aspect_ratio || 1;

  // Build placement style
  const placementStyle = aspectRatio !== 'auto'
    ? `top: ${placementTop}%; left: ${placementLeft}%; width: ${placementWidth}%; --placement-aspect-ratio: ${aspectRatio};`
    : `top: ${placementTop}%; left: ${placementLeft}%; width: ${placementWidth}%;`;

  return `
    <div class="mock-image-container mock-image-container--css" style="--frame-aspect-ratio: ${frameAspectRatio};">
      <div class="mock-image__frame-container" style="background-image: url(${addWidthParam(frameUrl, 800)});">
        <div class="mock-image__placement" style="${placementStyle}">
          <img
            src="${addWidthParam(artworkUrl, 480)}"
            srcset="${buildSrcSet(artworkUrl)}"
            sizes="(min-width: 750px) 160px, 150px"
            alt="${escapeHtml(alt)}"
            class="mock-image__artwork"
            loading="${loading}"
            ${fetchpriority !== 'auto' ? `fetchpriority="${fetchpriority}"` : ''}
          />
        </div>
      </div>
    </div>
  `;
}

/**
 * Build simple image HTML without mock
 */
function buildSimpleImageHtml(imageUrl, alt, loading, fetchpriority) {
  if (!imageUrl) {
    return `
      <div class="collection-card-image-placeholder" style="aspect-ratio: 3/4; background-color: #f5f5f5; display: flex; align-items: center; justify-content: center;">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" style="opacity: 0.3;">
          <path d="M42 38V10C42 8.9 41.1 8 40 8H8C6.9 8 6 8.9 6 10V38C6 39.1 6.9 40 8 40H40C41.1 40 42 39.1 42 38ZM17 27L21 31.51L26 25L33 34H15L17 27Z" fill="currentColor"/>
        </svg>
      </div>
    `;
  }

  return `
    <img
      src="${addWidthParam(imageUrl, 480)}"
      srcset="${buildSrcSet(imageUrl)}"
      sizes="(min-width: 750px) 160px, 150px"
      alt="${escapeHtml(alt)}"
      class="product-card__image collection-card-image"
      loading="${loading}"
      ${fetchpriority !== 'auto' ? `fetchpriority="${fetchpriority}"` : ''}
      style="width: 100%; height: auto;"
    />
  `;
}

/**
 * Add width param to image URL
 */
function addWidthParam(url, width) {
  if (!url) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}width=${width}&quality=60`;
}

/**
 * Build srcset for responsive images
 */
function buildSrcSet(url) {
  if (!url) return '';
  return [
    `${addWidthParam(url, 200)} 200w`,
    `${addWidthParam(url, 400)} 400w`,
    `${addWidthParam(url, 600)} 600w`,
    `${addWidthParam(url, 800)} 800w`
  ].join(', ');
}

/**
 * Extract handle from product
 */
function extractHandle(product) {
  if (product.handle && !product.handle.match(/^\d+$/)) {
    return product.handle;
  }
  if (product.url) {
    const match = product.url.match(/\/products\/([^?#/]+)/);
    if (match) return match[1];
  }
  return '';
}

/**
 * Get currency for current market
 */
function getMarketCurrency() {
  const country = window.Shopify?.country?.toLowerCase();
  if (!country) return 'GBP';

  const countryToCurrency = {
    'gb': 'GBP', 'uk': 'GBP',
    'us': 'USD',
    'au': 'AUD',
    'ca': 'CAD',
    'nz': 'NZD',
    // EU countries use EUR
    'de': 'EUR', 'fr': 'EUR', 'es': 'EUR', 'it': 'EUR', 'nl': 'EUR',
    'be': 'EUR', 'at': 'EUR', 'pt': 'EUR', 'ie': 'EUR', 'fi': 'EUR',
    'gr': 'EUR', 'lu': 'EUR', 'mt': 'EUR', 'cy': 'EUR', 'sk': 'EUR',
    'si': 'EUR', 'ee': 'EUR', 'lv': 'EUR', 'lt': 'EUR',
  };

  return countryToCurrency[country] || 'GBP';
}

/**
 * Get locale for price formatting
 */
function getLocaleForCurrency(currency) {
  const currencyToLocale = {
    'GBP': 'en-GB',
    'USD': 'en-US',
    'EUR': 'de-DE',
    'AUD': 'en-AU',
    'CAD': 'en-CA',
    'NZD': 'en-NZ',
  };
  return currencyToLocale[currency] || 'en-GB';
}

/**
 * Format price with currency
 * Note: prices from API are in decimal format (e.g., 29.99), not cents
 */
function formatPrice(price, basePricing) {
  if (!price) return '';
  // Use product's currency if available, otherwise derive from market
  const currency = basePricing?.currency || getMarketCurrency();
  const locale = getLocaleForCurrency(currency);
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(price);
  } catch {
    return `£${price.toFixed(2)}`;
  }
}

/**
 * Escape HTML special characters
 */
function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

class RelatedProducts extends HTMLElement {
  constructor() {
    super();
    this.loading = false;
    this.products = [];
    this.currentPage = 0;
    this.totalPages = 0;
    this.hasNext = false;
  }

  connectedCallback() {
    this.limit = parseInt(this.getAttribute('data-limit')) || 4;
    this.displayMode = this.getAttribute('data-display-mode') || 'grid';
    this.sectionId = this.getAttribute('data-section-id');
    this.productHandle = this.getAttribute('data-product-handle');

    // Check if we have a product handle
    if (!this.productHandle) {
      console.warn('[RelatedProducts] No product handle provided, hiding section');
      this.hideSection();
      return;
    }

    // Strategy: Preemptive loading during idle time, but fallback to scroll-based loading
    // This eliminates loading state for most users while not blocking critical content

    // Set up Intersection Observer as fallback (if user scrolls before preemptive load completes)
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.loading && !this.loaded) {
            this.loadProducts(0);
            this.observer.disconnect();
          }
        });
      }, {
        rootMargin: '400px' // Increased from 200px for earlier loading if user scrolls
      });

      this.observer.observe(this);
    }

    // Preemptive loading: Load during idle time after page is interactive
    // Priority: Lower than SKU picker and other critical above-the-fold content
    this.schedulePreemptiveLoad();
  }

  /**
   * Schedule preemptive loading of related products during browser idle time
   * Waits for page load and uses requestIdleCallback for lowest priority
   */
  schedulePreemptiveLoad() {
    // Wait for page to be fully loaded
    if (document.readyState === 'complete') {
      this.queuePreemptiveLoad();
    } else {
      window.addEventListener('load', () => {
        this.queuePreemptiveLoad();
      }, { once: true });
    }
  }

  /**
   * Queue the preemptive load using requestIdleCallback
   * This ensures it doesn't interfere with critical rendering or interactions
   */
  queuePreemptiveLoad() {
    // Use requestIdleCallback if available (preferred for lowest priority)
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        if (!this.loading && !this.loaded) {
          // Add a small delay to ensure SKU picker has priority
          setTimeout(() => {
            if (!this.loading && !this.loaded) {
              console.log('[RelatedProducts] Preemptively loading during idle time');
              this.loadProducts(0);
            }
          }, 100);
        }
      }, { timeout: 3000 }); // Max 3s timeout to ensure it runs eventually
    } else {
      // Fallback: Use setTimeout with delay for browsers without requestIdleCallback
      setTimeout(() => {
        if (!this.loading && !this.loaded) {
          console.log('[RelatedProducts] Preemptively loading (setTimeout fallback)');
          this.loadProducts(0);
        }
      }, 1500); // Load 1.5s after page load as fallback
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  /**
   * Load related products from similar products API (CLIP visual similarity)
   * @param {number} page - The page number to load (0-based)
   */
  async loadProducts(page = 0) {
    if (this.loading) {
      return;
    }

    this.loading = true;

    try {
      const result = await fetchSimilarProducts(this.productHandle, {
        page,
        limit: 16,
        minDistance: 0
      });

      if (result.error) {
        throw new Error(result.error);
      }

      const products = result.products || [];
      this.hasNext = result.hasMore || false;
      this.currentPage = page;

      if (products.length === 0) {
        console.warn('[RelatedProducts] No related products found in response');
        if (page === 0) {
          this.hideSection();
        }
        return;
      }

      // Render products using frontend rendering
      this.renderProductsFromJson(products, page);
      this.showSection();
      this.updateLoadMoreButton();

      // Notify other components (e.g. room-background-mock diptych mode)
      document.dispatchEvent(new CustomEvent('related-products:loaded', {
        bubbles: true,
        detail: { products, productHandle: this.productHandle }
      }));
    } catch (error) {
      console.error('[RelatedProducts] Error loading related products:', error);
      if (page === 0) {
        this.hideSection();
      }
    } finally {
      this.loading = false;
      this.loaded = true;
    }
  }

  /**
   * Render products from JSON data using frontend rendering
   * @param {Array} products - Array of product data from API
   * @param {number} page - Current page (0-based)
   */
  renderProductsFromJson(products, page) {
    const targetContainer = this.querySelector('.related-products__grid');
    if (!targetContainer) return;

    if (products.length === 0) {
      if (page === 0) {
        this.hideSection();
      }
      return;
    }

    // Build HTML from product data
    const html = buildProductGridHtml(products);

    if (page === 0) {
      // First page - replace content
      targetContainer.innerHTML = html;
    } else {
      // Subsequent pages - append products with animation
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      const newItems = tempDiv.querySelectorAll('.product-grid__item, .resource-list__item');
      newItems.forEach(item => {
        item.classList.add('resource-list__item--new');
        targetContainer.appendChild(item);
        setTimeout(() => {
          item.classList.remove('resource-list__item--new');
        }, 300);
      });
    }

    // Reinitialize Rivo wishlist buttons for dynamically added products
    this.reinitializeWishlistButtons();
  }

  /**
   * Reinitialize Rivo wishlist buttons after dynamic content is added
   */
  reinitializeWishlistButtons() {
    try {
      if (window.RivoProfileAlpine) {
        const rivoAccountWidget = document.getElementById('rivo-account-slideout');
        if (rivoAccountWidget) {
          window.RivoProfileAlpine.$data(rivoAccountWidget).initializeFavoriteButtons();
        }
      }
    } catch (err) {
      console.warn('[RelatedProducts] Error reinitializing wishlist buttons:', err);
    }
  }

  /**
   * Update the Load More button visibility and state
   */
  updateLoadMoreButton() {

    let loadMoreButton = this.querySelector('.related-products__load-more');

    if (this.hasNext) {
      // Create button if it doesn't exist
      if (!loadMoreButton) {
        loadMoreButton = document.createElement('button');
        loadMoreButton.className = 'button button-secondary related-products__load-more';
        loadMoreButton.textContent = 'Load More';
        loadMoreButton.addEventListener('click', () => this.handleLoadMore());

        // Find the content container and append button after it
        const content = this.querySelector('.related-products__content');
        if (content) {
          content.appendChild(loadMoreButton);
        }
      }

      // Show and enable button
      loadMoreButton.style.display = 'block';
      loadMoreButton.disabled = false;
      loadMoreButton.textContent = 'Load More';
    } else if (loadMoreButton) {
      // Hide button if no more pages
      loadMoreButton.style.display = 'none';
    }
  }

  /**
   * Handle Load More button click
   */
  async handleLoadMore() {

    const button = this.querySelector('.related-products__load-more');
    if (button) {
      button.disabled = true;
      button.textContent = 'Loading...';
    }

    // Load next page
    await this.loadProducts(this.currentPage + 1);
  }

  /**
   * Hide the section
   */
  hideSection() {
    const section = this.closest('.shopify-section');
    if (section) {
      section.style.display = 'none';
    } else {
      console.warn('[RelatedProducts] No parent section found to hide');
    }
  }

  /**
   * Show the section
   */
  showSection() {
    const section = this.closest('.shopify-section');
    if (section) {
      section.style.display = '';
    } else {
      console.warn('[RelatedProducts] No parent section found to show');
    }
  }
}

// Register the custom element
if (!customElements.get('related-products')) {
  customElements.define('related-products', RelatedProducts);
} else {
}
